import { filter, find, map, nth } from 'lodash';
import { useEffect, useMemo } from 'react';
import { createContext, useContext, useState } from 'react';
import useFindIdCardByTagId from '../hooks/useFindIdCardByTagId';
import useFindProjectIdCardByProjectId from '../hooks/useFindProjectIdCardByProjectId';

import useDropdownOptions from '../hooks/useDropdownOptions';
import useSearchIdCards from '../hooks/useSearchIdCards';
import { useLocation } from 'react-router-dom';
import useGetEquipmentByTagNr from '../hooks/useFindEquipmentByNrTagId';

const IdCatalogSearchContext = createContext(null);

const generateFilterOption = (entity, filterType, value) => {
  const defaults = { min: 0, max: 10e4, unit: 'unit', type: 'NUMERIC' };
  const id = FilterOptionsEnum.Equipment === filterType ? entity.tagNr : entity.name;

  return {
    id: id,
    name: id,
    min: entity.min || defaults.min,
    max: entity.max || defaults.max,
    unit: entity.unit || defaults.unit,
    type: entity.type || defaults.type,
    value: value || {
      min: entity.min || defaults.min,
      max: entity.max || defaults.max,
    },
  };
};
const updateListElement = (list, id, newElement) => map(list, (element) => (id === element.id ? newElement : element));

const getInitialFilter = () => ({
  searchInput: '',
  projectId: undefined,
  equipmentType: undefined,
  familyId: undefined,
  filterOptions: [],
  refreshToggle: false,
});

const extractSearchPayloadFromSearchFilters = (searchFilter, filterType = FilterOptionsEnum.Module) => ({
  name: searchFilter.searchInput,
  projectId: searchFilter.projectId,
  family: searchFilter.familyId,
  characteristics:
    filterType === FilterOptionsEnum.Module
      ? searchFilter.filterOptions.map((filterOption) => ({
          name: filterOption.id,
          type: filterOption.type,
          value: `${filterOption.value.min},${filterOption.value.max}`,
        }))
      : [],
  equipment:
    filterType === FilterOptionsEnum.Equipment
      ? searchFilter.filterOptions.reduce(
          (equipment, filterOption) => ({
            ...equipment,
            [filterOption.id]: `${filterOption.value.min},${filterOption.value.max}`,
          }),
          {}
        )
      : undefined,
});

const getCharacteristicById = (id, dropdownsOptions) => find(dropdownsOptions.characteristics.entity, { name: id });

const isStandardizationRouteOpened = (pathname) => /\/standardization\/project\/.+/.test(pathname);

export const FilterOptionsEnum = {
  Equipment: 'equipment',
  Module: 'module',
};

export const IdCatalogSearchProvider = ({ children }) => {
  const [filterOptionId, setFilterOptionId] = useState(FilterOptionsEnum.Module);
  const [isSideMenuCollapsed, setIsSideMenuCollapsed] = useState(true);
  const [isCreateProjectIdCardModalOpened, setIsCreateProjectIdCardModalOpened] = useState(false);
  const [createProjectIdCardProjectId, setCreateProjectIdCardProjectId] = useState(null);
  const [sideMenuZIndex, setSideMenuZIndex] = useState(undefined);

  const location = useLocation();

  const [searchFilter, setSearchFilter] = useState(getInitialFilter());
  const [selectedCatalogItems, setSelectedCatalogItems] = useState([]);
  const dropdownsOptions = useDropdownOptions({
    familyId: searchFilter.familyId,
    filterOptions: searchFilter.filterOptions,
  });

  let firstIdCard = useFindIdCardByTagId(nth(selectedCatalogItems, 0));
  let secondIdCard = useFindIdCardByTagId(nth(selectedCatalogItems, 1));
  let firstProjectIdCard = useFindProjectIdCardByProjectId(nth(selectedCatalogItems, 0));
  let secondProjectIdCard = useFindProjectIdCardByProjectId(nth(selectedCatalogItems, 1));
  const firstSlotCatalogItem = firstIdCard?.idCard ? firstIdCard : firstProjectIdCard?.idCard ? firstProjectIdCard : {idCard: null, isLoading: false, error: null};
  const secondSlotCatalogItem = secondIdCard?.idCard ? secondIdCard : secondProjectIdCard?.idCard ? secondProjectIdCard : {idCard: null, isLoading: false, error: null};

  const { equipment, isLoading: isEquipmentLoading } = useGetEquipmentByTagNr(searchFilter.equipmentType);

  const {
    idCardsResponse,
    isLoading: areIdCardsLoading,
    error: idCardsResponseError,
    searchIdCards,
  } = useSearchIdCards();

  const updateSearchFilterField = (key, value) => {
    setSearchFilter((current) => ({ ...current, [key]: value }));
  };

  const addFilterOption = (id) => {
    const characteristic = getCharacteristicById(id, dropdownsOptions);
    updateSearchFilterField('filterOptions', [
      ...searchFilter.filterOptions,
      generateFilterOption(characteristic, filterOptionId),
    ]);
  };

  const addEquipmentFilterOption = (payload) => {
    updateSearchFilterField('filterOptions', [
      ...searchFilter.filterOptions,
      generateFilterOption(payload, filterOptionId),
    ]);
  };

  const removeFilterOption = (id) => {
    updateSearchFilterField(
      'filterOptions',
      filter(searchFilter.filterOptions, (it) => it.id !== id)
    );
  };

  const removeAllFilterOptions = () => updateSearchFilterField('filterOptions', []);

  const updateFilterOptionValue = (id, value) => {
    const { filterOptions } = searchFilter;
    const entity =
      filterOptionId === FilterOptionsEnum.Equipment
        ? equipment
        : find(dropdownsOptions.characteristics.entity, { name: id });

    const updatedFilterOptions = updateListElement(
      filterOptions,
      id,
      generateFilterOption(entity, filterOptionId, value)
    );
    updateSearchFilterField('filterOptions', updatedFilterOptions);
  };

  const removeCatalogItem = (tagId) => {
      setSelectedCatalogItems((current) => filter(current, (value) => tagId !== value.tagId));
  };

  const addCatalogItem = (tagId, isFpso) => {
    setSelectedCatalogItems((current) => [{tagId:tagId, isFpso:isFpso}]);
    setIsSideMenuCollapsed(false);
  };

  const addSecondCatalogItem = (tagId, isFpso) => {
    setSelectedCatalogItems((current) => [...current, {tagId:tagId, isFpso:isFpso}]);
  };

  const clearCatalogItems = () => setSelectedCatalogItems([]);

  const showCompare = selectedCatalogItems.length === 1 && !selectedCatalogItems[0].isFpso;

  const idCards = useMemo(
    () => idCardsResponse?.data || [],
    [idCardsResponse, areIdCardsLoading, idCardsResponseError]
  );

  const catalogResult = {
    idCards,
    isLoading: areIdCardsLoading,
    error: idCardsResponseError,
  };

  const addProjectIdCard = (projectId) => {
      setIsCreateProjectIdCardModalOpened(true);
      setCreateProjectIdCardProjectId(projectId);
  };

  useEffect(() => {
    updateSearchFilterField('filterOptions', []);
  }, [searchFilter.familyId]);

  useEffect(() => {
    searchIdCards(extractSearchPayloadFromSearchFilters(searchFilter, filterOptionId));
  }, [searchFilter]);

  useEffect(() => {
    if (equipment === undefined) {
      return;
    }

    addEquipmentFilterOption(equipment);
  }, [equipment?.tagNr, isEquipmentLoading]);

  const shouldRenderModuleItemAddIcon = useMemo(
    () => isStandardizationRouteOpened(location.pathname),
    [location.pathname]
  );

  return (
    <IdCatalogSearchContext.Provider
      value={{
        searchFilter,
        clearCatalogItems,
        updateSearchFilterField,
        isSideMenuCollapsed,
        sideMenuZIndex,
        setIsSideMenuCollapsed,
        addFilterOption,
        removeFilterOption,
        updateFilterOptionValue,
        removeAllFilterOptions,
        setFilterOptionId,
        removeCatalogItem,
        addCatalogItem,
        addSecondCatalogItem,
        filterOptionId,
        selectedCatalogItems,
        showCompare,
        catalogResult,
        firstSlotCatalogItem,
        secondSlotCatalogItem,
        dropdownsOptions,
        shouldRenderModuleItemAddIcon,
        addProjectIdCard,
        isCreateProjectIdCardModalOpened,
        setIsCreateProjectIdCardModalOpened,
        createProjectIdCardProjectId,
      }}>
      {children}
    </IdCatalogSearchContext.Provider>
  );
};

export const useIdCatalogSearch = () => useContext(IdCatalogSearchContext);
